import { useEffect, useMemo, useState } from 'react';
import { PageHeader, DataTable, FilterBar, Stat, Stats } from '../../components/office';
import { Button } from '@verlo/shared/components/ui/button';
import { Input } from '@verlo/shared/components/ui/input';
import { Label } from '@verlo/shared/components/ui/label';
import { Switch } from '@verlo/shared/components/ui/switch';
import { Signal } from '@verlo/shared/components/ui/signal';
import { Segmented } from '@verlo/shared/components/ui/segmented';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@verlo/shared/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@verlo/shared/components/ui/select';
import { modifiersApi, menuApi } from '@verlo/shared/services/api';
import { useApp } from '@verlo/shared/contexts/AppContext';
import { useSite } from '../../contexts/SiteContext';
import { toast } from 'sonner';
import { Plus, Loader2 } from 'lucide-react';

/**
 * Menu › Modifiers — the library (modifiers plan, 21 Sep 2026). Everything the till's
 * "Anything else" can find: a name, a price (negative allowed here, never on free text),
 * what kind of thing it is, the group it files under, the revenue line it lands on (or
 * inherit the item's), and whether the till can count it. An item's own groups — Spirit,
 * Heat, Extras on a Bloody Mary — are set on the item, in Menu › Items.
 */

const KINDS = [
  { value: 'extra', label: 'Extra', hint: 'Adds something, usually with a price' },
  { value: 'option', label: 'Option', hint: 'A way of having it: a spirit, a measure' },
  { value: 'instruction', label: 'Instruction', hint: 'No charge: "no ice", "well done"' },
];
const LINES = [
  { value: 'inherit', label: 'Same as the item' },
  { value: 'food', label: 'Food' },
  { value: 'drink', label: 'Drink' },
  { value: 'other', label: 'Other' },
];
const EMPTY = { name: '', price: '0', kind: 'extra', group: '', revenue_line: 'inherit', qty: false, is_active: true };

const ModifiersPage = () => {
  const { formatCurrency } = useApp();
  // Modifiers are site-scoped rows, so the list and the create follow the header's site
  // picker — the same fix as the items beside them (#92).
  const { site } = useSite();
  const [rows, setRows] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [group, setGroup] = useState('all');
  const [editing, setEditing] = useState(null);   // null | EMPTY (new) | row
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(null);   // the modifier whose Remove is being confirmed

  const load = () => {
    setLoading(true);
    Promise.all([modifiersApi.getAll(site), menuApi.getAll(undefined, site).catch(() => ({ data: [] }))])
      .then(([m, i]) => { setRows(m.data || []); setItems(i.data || []); })
      .catch(() => toast.error("Couldn't load the modifiers"))
      .finally(() => setLoading(false));
  };
  useEffect(load, [site]); // eslint-disable-line react-hooks/exhaustive-deps

  const groups = useMemo(() => [...new Set(rows.map((r) => r.group).filter(Boolean))].sort(), [rows]);
  // Where an item's own groups already use the same words — so the office can see a
  // library modifier is also a cell on a Bloody Mary.
  // Keyed by site and name: with every venue in the header, one site's "Fried egg" must not
  // count another site's dishes (its Remove only touches its own site's).
  const usedOn = useMemo(() => {
    const map = {};
    for (const it of items) {
      for (const g of it.modifier_groups || []) for (const o of g.options || []) {
        const k = `${it.site_id || ''}|${(o.name || '').toLowerCase()}`;
        (map[k] = map[k] || []).push(it.name);
      }
      for (const m of it.modifiers || []) {
        const n = (typeof m === 'string' ? m : m?.name || '').toLowerCase();
        const k = `${it.site_id || ''}|${n}`;
        if (n) (map[k] = map[k] || []).push(it.name);
      }
    }
    return map;
  }, [items]);

  const filtered = rows.filter((r) => (group === 'all' || r.group === group) && (!search || r.name.toLowerCase().includes(search.toLowerCase())));
  const priced = rows.filter((r) => Math.round((r.price || 0) * 100) !== 0).length;

  const openNew = () => { setForm(EMPTY); setEditing(EMPTY); };
  const openEdit = (r) => { setForm({ ...EMPTY, ...r, price: String(r.price ?? 0), revenue_line: r.revenue_line || 'inherit' }); setEditing(r); };

  const save = async () => {
    const payload = {
      name: form.name.trim(), price: parseFloat(form.price) || 0, kind: form.kind, group: form.group.trim(),
      revenue_line: form.revenue_line === 'inherit' ? null : form.revenue_line, qty: !!form.qty, is_active: !!form.is_active,
      category: form.category || 'add_on',
    };
    if (!payload.name) { toast.error('A modifier needs a name'); return; }
    setSaving(true);
    try {
      if (editing?.id) await modifiersApi.update(editing.id, payload); else await modifiersApi.create(payload, site);
      toast.success(editing?.id ? 'Modifier saved' : 'Modifier added');
      setEditing(null); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save");
    } finally { setSaving(false); }
  };

  // Remove (was Retire): it leaves the library and comes off the dishes that offer it. It asks
  // first, saying how many dishes that is; past orders keep it.
  const remove = async (r) => {
    try {
      const { data } = await modifiersApi.delete(r.id);
      toast.success(data?.dishes ? `${r.name} removed, and taken off ${data.dishes} dish${data.dishes === 1 ? '' : 'es'}` : `${r.name} removed`);
      setRemoving(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't remove it"); }
  };
  // The dishes at the modifier's own site that offer it (any site, for a row with none).
  const dishesWith = (r) => {
    const name = (r?.name || '').toLowerCase();
    if (r?.site_id) return usedOn[`${r.site_id}|${name}`] || [];
    return Object.entries(usedOn).filter(([k]) => k.endsWith(`|${name}`)).flatMap(([, v]) => v);
  };
  // Another modifier of the same name at the site: removing this one leaves the dishes alone.
  const twinOf = (r) => r && rows.find((x) => x.id !== r.id && x.is_active !== false
    && (x.site_id || '') === (r.site_id || '') && (x.name || '').trim().toLowerCase() === (r.name || '').trim().toLowerCase());

  const columns = [
    { key: 'name', label: 'Modifier', sortable: true, render: (r) => <span className={r.is_active === false ? 'text-ink-faint line-through' : 'font-semibold text-ink'}>{r.name}</span> },
    { key: 'kind', label: 'Kind', sortable: true, render: (r) => <span className="text-ink-soft">{KINDS.find((k) => k.value === (r.kind || 'extra'))?.label}</span> },
    { key: 'group', label: 'Group', sortable: true, render: (r) => <span className="text-ink-soft">{r.group || <span className="text-ink-faint">—</span>}</span> },
    { key: 'price', label: 'Price', align: 'right', mono: true, sortable: true, render: (r) => Math.round((r.price || 0) * 100) === 0 ? <span className="text-ink-faint">£0</span> : <span className={r.price < 0 ? 'text-signal-warn' : ''}>{r.price > 0 ? '+' : '−'}{formatCurrency(Math.abs(r.price))}</span> },
    { key: 'revenue_line', label: 'Revenue line', render: (r) => r.revenue_line ? <Signal tone={r.revenue_line === 'drink' ? 'info' : r.revenue_line === 'food' ? 'ok' : 'warn'} dot={false}>{r.revenue_line}</Signal> : <span className="text-ink-faint">item's</span> },
    { key: 'qty', label: 'Counts', render: (r) => r.qty ? <span className="text-ink-soft">×n</span> : '' },
    { key: 'used', label: 'On items', render: (r) => { const u = dishesWith(r); return u.length ? <span className="text-ink-soft" title={u.join(', ')}>{u.length} item{u.length === 1 ? '' : 's'}</span> : <span className="text-ink-faint">library only</span>; } },
  ];

  return (
    <div data-testid="modifiers-page">
      <PageHeader
        title="Modifiers"
        purpose="Everything the till can add to an item: what it's called, what it costs, and which line the money lands on."
        actions={<Button className="bg-verlo hover:bg-verlo-hover" onClick={openNew} data-testid="modifier-add"><Plus size={16} className="mr-1.5" />Add modifier</Button>}
      />
      <Stats className="mb-6">
        <Stat label="In the library" value={rows.length} testId="modifiers-count" />
        <Stat label="Priced" value={priced} hint="the rest are instructions" />
        <Stat label="Groups" value={groups.length} hint={groups.slice(0, 3).join(' · ') || undefined} />
      </Stats>
      <FilterBar right={<span>{filtered.length} shown</span>}>
        <Input placeholder="Search modifiers…" value={search} onChange={(e) => setSearch(e.target.value)} className="h-9 w-64 bg-white" data-testid="modifiers-search" />
        {groups.length > 0 && (
          <Segmented value={group} onChange={setGroup} options={[{ value: 'all', label: 'All' }, ...groups.map((g) => ({ value: g, label: g }))]} />
        )}
      </FilterBar>
      <DataTable
        columns={columns} rows={filtered} rowKey="id" loading={loading} onRowClick={openEdit}
        actions={(r) => <Button size="sm" variant="ghost" className="text-signal-stop hover:text-signal-stop" onClick={(e) => { e.stopPropagation(); setRemoving(r); }} data-testid={`modifier-remove-${r.id}`}>Remove</Button>}
        empty={{ word: 'Modifiers', title: 'Nothing in the library yet', action: <Button variant="outline" onClick={openNew}>Add the first</Button>, children: 'The till searches this list when a server types in "Anything else".' }}
        testId="modifiers-table"
      />

      <Dialog open={!!editing} onOpenChange={(v) => !saving && !v && setEditing(null)}>
        <DialogContent size="form" data-testid="modifier-dialog">
          <DialogHeader>
            <DialogTitle>{editing?.id ? 'Edit modifier' : 'Add modifier'}</DialogTitle>
            <DialogDescription>A library modifier is anything the till can find by typing. An item's own choices (Spirit, Heat) are set on the item.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-1">
            <div className="grid gap-1.5">
              <Label className="text-xs">Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Bacon rasher" data-testid="modifier-name" autoFocus />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-1.5">
                <Label className="text-xs">Price</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-soft">£</span>
                  <Input type="number" inputMode="decimal" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="pl-7 font-mono tabular-nums" data-testid="modifier-price" />
                </div>
                <p className="text-[11.5px] text-ink-faint">Negative takes money off (Virgin −2.00). Zero is an instruction.</p>
              </div>
              <div className="grid gap-1.5">
                <Label className="text-xs">Group</Label>
                <Input value={form.group} onChange={(e) => setForm({ ...form, group: e.target.value })} placeholder="e.g. Drinks, Coffee, Cooking…" list="modifier-groups" data-testid="modifier-group" />
                <datalist id="modifier-groups">{groups.map((g) => <option key={g} value={g} />)}</datalist>
              </div>
            </div>
            <div className="grid gap-1.5">
              <Label className="text-xs">Kind</Label>
              <Segmented value={form.kind} onChange={(v) => setForm({ ...form, kind: v })} options={KINDS.map((k) => ({ value: k.value, label: k.label, testId: `modifier-kind-${k.value}` }))} stretch />
              <p className="text-[11.5px] text-ink-faint">{KINDS.find((k) => k.value === form.kind)?.hint}</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-1.5">
                <Label className="text-xs">Revenue line</Label>
                <Select value={form.revenue_line} onValueChange={(v) => setForm({ ...form, revenue_line: v })}>
                  <SelectTrigger className="h-10" data-testid="modifier-line"><SelectValue /></SelectTrigger>
                  <SelectContent>{LINES.map((l) => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}</SelectContent>
                </Select>
                <p className="text-[11.5px] text-ink-faint">A bacon rasher on a cocktail is still food.</p>
              </div>
              <div className="grid gap-3 pt-1">
                <label className="flex items-center justify-between gap-3 text-[13.5px] text-ink">The till can count it (×2)<Switch checked={!!form.qty} onCheckedChange={(v) => setForm({ ...form, qty: v })} data-testid="modifier-qty" /></label>
                <label className="flex items-center justify-between gap-3 text-[13.5px] text-ink">Offered on the till<Switch checked={form.is_active !== false} onCheckedChange={(v) => setForm({ ...form, is_active: v })} data-testid="modifier-active" /></label>
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setEditing(null)} disabled={saving}>Cancel</Button>
            <Button className="bg-verlo hover:bg-verlo-hover" onClick={save} disabled={saving} data-testid="modifier-save">{saving && <Loader2 size={14} className="mr-2 animate-spin" />}Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!removing} onOpenChange={(o) => { if (!o) setRemoving(null); }}>
        <DialogContent className="max-w-sm" data-testid="modifier-remove-dialog">
          <DialogHeader>
            <DialogTitle>Remove {removing?.name}?</DialogTitle>
            <DialogDescription>
              {twinOf(removing)
                ? `There's another ${removing.name} in the library, so the dishes keep it. Past orders keep it too.`
                : dishesWith(removing).length
                ? `It comes off ${dishesWith(removing).length} dish${dishesWith(removing).length === 1 ? '' : 'es'}: ${dishesWith(removing).slice(0, 4).join(', ')}${dishesWith(removing).length > 4 ? '…' : ''}. Past orders keep it.`
                : 'No dish offers it. Past orders keep it.'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRemoving(null)}>Cancel</Button>
            <Button className="bg-signal-stop text-white hover:bg-signal-stop/90" onClick={() => remove(removing)} data-testid="modifier-remove-confirm">Remove</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ModifiersPage;
